import type {
  IDocumentReaderUploadBatchCreated,
  IDocumentReaderUploadFile,
  IDocumentReaderUploadFileInput,
  IDocumentReaderUploadLimits,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';
import { S3FileNameHelper } from '@commenda/utils/files/s3-file-name-helper';

import {
  DataRoomFileState,
  type IDataRoomFileProgress,
  type IDataRoomLocalFile,
  type IDataRoomPartRange,
  type IDataRoomResumeMatch,
  type IDataRoomUploadProgress,
} from './types';

export class DataRoomUploadHelper {
  static readonly PARTS_IN_FLIGHT_PER_FILE = 4;

  static readonly PARTS_IN_FLIGHT = 6;

  static readonly RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000];

  static readonly MAX_VISIBLE_FILE_ROWS = 50;

  static readonly MAX_LISTED_NAMES = 5;

  static readonly ZIP_ACCEPT = '.zip,application/zip';

  private static readonly ZIP_MIME_TYPES: readonly string[] = [
    'application/zip',
    'application/x-zip-compressed',
  ];

  private static readonly BYTES_PER_MB = 1_000_000;

  private static readonly BYTES_PER_GB = 1_000_000_000;

  private static readonly MAX_FILE_NAME_LENGTH = 255;

  private static readonly MAX_RELATIVE_PATH_LENGTH = 1_024;

  /** Byte range of one part: every part is `partSizeBytes` long except the last. */
  static partRange(
    partNumber: number,
    sizeBytes: number,
    partSizeBytes: number
  ): IDataRoomPartRange {
    const start = (partNumber - 1) * partSizeBytes;
    return {
      partNumber,
      start,
      end: Math.min(start + partSizeBytes, sizeBytes),
    };
  }

  static missingPartNumbers(file: Readonly<IDataRoomLocalFile>): number[] {
    return Array.from(
      { length: file.partCount },
      (_, index) => index + 1
    ).filter((partNumber) => !file.storedEntityTags.has(partNumber));
  }

  static storedBytes(file: Readonly<IDataRoomLocalFile>): number {
    return [...file.storedEntityTags.keys()].reduce((sum, partNumber) => {
      const range = DataRoomUploadHelper.partRange(
        partNumber,
        file.selectedFile.file.size,
        file.partSizeBytes
      );
      return sum + (range.end - range.start);
    }, 0);
  }

  /** "4.8 GB" from 1 GB up, "40.0 MB" below, one decimal either way. */
  static formatBytes(bytes: number): string {
    return bytes >= DataRoomUploadHelper.BYTES_PER_GB
      ? `${(bytes / DataRoomUploadHelper.BYTES_PER_GB).toFixed(1)} GB`
      : `${(bytes / DataRoomUploadHelper.BYTES_PER_MB).toFixed(1)} MB`;
  }

  /** "1.2 of 4.8 GB": both numbers in the unit of the total. */
  static formatProgress(sentBytes: number, totalBytes: number): string {
    const unit =
      totalBytes >= DataRoomUploadHelper.BYTES_PER_GB
        ? DataRoomUploadHelper.BYTES_PER_GB
        : DataRoomUploadHelper.BYTES_PER_MB;
    const label = unit === DataRoomUploadHelper.BYTES_PER_GB ? 'GB' : 'MB';
    return `${(sentBytes / unit).toFixed(1)} of ${(totalBytes / unit).toFixed(1)} ${label}`;
  }

  static percent(sentBytes: number, totalBytes: number): number {
    if (totalBytes <= 0) return 100;
    return Math.min(100, Math.floor((sentBytes / totalBytes) * 100));
  }

  static isZip(file: File): boolean {
    return (
      file.name.toLowerCase().endsWith('.zip') ||
      DataRoomUploadHelper.ZIP_MIME_TYPES.includes(file.type)
    );
  }

  /** A selection becomes a data room when it holds a zip or more files than one message takes. */
  static isDataRoomSelection(
    uploads: readonly CommendaAiUploadFile[],
    maxAttachments: number
  ): boolean {
    return (
      uploads.length > maxAttachments ||
      uploads.some(({ file }) => DataRoomUploadHelper.isZip(file))
    );
  }

  static acceptWithZip(accept: string): string {
    return `${accept},${DataRoomUploadHelper.ZIP_ACCEPT}`;
  }

  static totalBytes(uploads: readonly CommendaAiUploadFile[]): number {
    return uploads.reduce((sum, { file }) => sum + file.size, 0);
  }

  static splitEmpty(uploads: readonly CommendaAiUploadFile[]): {
    uploads: CommendaAiUploadFile[];
    emptyNames: string[];
  } {
    return {
      uploads: uploads.filter(({ file }) => file.size > 0),
      emptyNames: uploads
        .filter(({ file }) => file.size === 0)
        .map(({ path }) => path),
    };
  }

  /** The refusal to show before any request, or null when the room fits the limits. */
  static getLimitError(
    uploads: readonly CommendaAiUploadFile[],
    limits: Readonly<IDocumentReaderUploadLimits>
  ): string | null {
    if (uploads.length === 0)
      return 'Every file in this selection is empty, so there is nothing to upload.';
    const totalBytes = DataRoomUploadHelper.totalBytes(uploads);
    if (totalBytes > limits.maxRoomBytes)
      return `This data room is ${DataRoomUploadHelper.formatBytes(totalBytes)}; the limit is ${DataRoomUploadHelper.formatBytes(limits.maxRoomBytes)}.`;
    if (uploads.length > limits.maxFileCount)
      return `This data room has ${uploads.length} files; the limit is ${limits.maxFileCount}.`;
    return null;
  }

  static formatNameList(names: readonly string[]): string {
    const shown = names
      .slice(0, DataRoomUploadHelper.MAX_LISTED_NAMES)
      .join(', ');
    const hiddenCount = names.length - DataRoomUploadHelper.MAX_LISTED_NAMES;
    return hiddenCount > 0 ? `${shown} and ${hiddenCount} more` : shown;
  }

  /** One message naming the files a resume could not pair and those it can no longer finish. */
  static getResumeError(
    unmatchedNames: readonly string[],
    abortedNames: readonly string[]
  ): string | null {
    const messages = [
      unmatchedNames.length > 0
        ? `These files were not in your selection, so choose them too: ${DataRoomUploadHelper.formatNameList(unmatchedNames)}.`
        : null,
      abortedNames.length > 0
        ? `These files can no longer be finished, so cancel and upload the room again: ${DataRoomUploadHelper.formatNameList(abortedNames)}.`
        : null,
    ].filter((message): message is string => message !== null);
    return messages.length > 0 ? messages.join(' ') : null;
  }

  /** True when a dropped selection was read from inside a folder. */
  static hasFolderFiles(uploads: readonly CommendaAiUploadFile[]): boolean {
    return uploads.some(({ path }) => path.includes('/'));
  }

  static toFileInput({
    file,
    path,
  }: Readonly<CommendaAiUploadFile>): IDocumentReaderUploadFileInput {
    const fileName = S3FileNameHelper.sanitize(file.name).slice(
      0,
      DataRoomUploadHelper.MAX_FILE_NAME_LENGTH
    );
    const relativePath =
      path && path !== file.name
        ? path.slice(-DataRoomUploadHelper.MAX_RELATIVE_PATH_LENGTH)
        : undefined;
    return {
      fileName,
      sizeBytes: file.size,
      ...(relativePath ? { relativePath } : {}),
      ...(file.type ? { contentType: file.type } : {}),
    };
  }

  static splitBatches<T>(items: readonly T[], batchSize: number): T[][] {
    return Array.from(
      { length: Math.ceil(items.length / batchSize) },
      (_, index) => items.slice(index * batchSize, (index + 1) * batchSize)
    );
  }

  /** Pairs re-selected files with one batch's server files by name, size and last-modified time
   * (when the record kept it). Stored parts and their ETags seed each pair, so only missing parts go. */
  static matchResumeFiles(
    uploads: readonly CommendaAiUploadFile[],
    serverFiles: readonly IDocumentReaderUploadFile[],
    batchIndex: number,
    lastModifiedInBatch: readonly number[] | undefined
  ): IDataRoomResumeMatch {
    const localByKey = new Map<string, CommendaAiUploadFile[]>();
    uploads.forEach((upload) => {
      const input = DataRoomUploadHelper.toFileInput(upload);
      const key = DataRoomUploadHelper.matchKey(
        input.relativePath ?? input.fileName,
        upload.file.size,
        lastModifiedInBatch ? upload.file.lastModified : null
      );
      localByKey.set(key, [...(localByKey.get(key) ?? []), upload]);
    });

    const files: IDataRoomLocalFile[] = [];
    const unmatchedNames: string[] = [];
    serverFiles.forEach((serverFile) => {
      const position = DataRoomUploadHelper.filePosition(serverFile.id);
      const lastModified = lastModifiedInBatch?.[position];
      const key = DataRoomUploadHelper.matchKey(
        serverFile.relativePath ?? serverFile.fileName,
        serverFile.sizeBytes,
        lastModifiedInBatch ? (lastModified ?? -1) : null
      );
      const selectedFile = localByKey.get(key)?.shift();
      if (!selectedFile) {
        unmatchedNames.push(serverFile.relativePath ?? serverFile.fileName);
        return;
      }
      files.push({
        selectedFile,
        fileId: serverFile.id,
        batchIndex,
        partCount: serverFile.partCount,
        partSizeBytes: serverFile.partSizeBytes,
        storedEntityTags: new Map(
          serverFile.uploadedParts.map((part) => [
            part.partNumber,
            part.entityTag,
          ])
        ),
      });
    });

    return { files, unmatchedNames };
  }

  /** Pairs one created batch with the files sent in it. Empty files are never sent, so the backend's
   * list is the request in order; any other length means the pairing cannot be trusted. */
  static pairCreatedFiles(
    uploads: readonly CommendaAiUploadFile[],
    created: Readonly<IDocumentReaderUploadBatchCreated>
  ): IDataRoomLocalFile[] | null {
    if (created.files.length !== uploads.length) return null;
    return created.files.map((createdFile, index) => ({
      selectedFile: uploads[index],
      fileId: createdFile.id,
      batchIndex: created.batchIndex,
      partCount: createdFile.partCount,
      partSizeBytes: createdFile.partSizeBytes,
      storedEntityTags: new Map<number, string>(),
    }));
  }

  static toFileProgress(
    file: Readonly<IDataRoomLocalFile>
  ): IDataRoomFileProgress {
    return {
      fileId: file.fileId,
      name: file.selectedFile.path,
      sizeBytes: file.selectedFile.file.size,
      sentBytes: DataRoomUploadHelper.storedBytes(file),
      state: DataRoomFileState.UPLOADING,
      error: null,
    };
  }

  /** A file the server already holds in full needs no local copy to show as stored. */
  static toStoredRow(
    serverFile: Readonly<IDocumentReaderUploadFile>
  ): IDataRoomFileProgress {
    return {
      fileId: serverFile.id,
      name: serverFile.relativePath ?? serverFile.fileName,
      sizeBytes: serverFile.sizeBytes,
      sentBytes: serverFile.sizeBytes,
      state: DataRoomFileState.STORED,
      error: null,
    };
  }

  static summarise(
    files: readonly IDataRoomFileProgress[]
  ): IDataRoomUploadProgress {
    return {
      files: [...files],
      sentBytes: files.reduce((sum, file) => sum + file.sentBytes, 0),
      totalBytes: files.reduce((sum, file) => sum + file.sizeBytes, 0),
      isStored:
        files.length > 0 &&
        files.every((file) => file.state === DataRoomFileState.STORED),
    };
  }

  /** Failed files first, then those still moving, so the rows worth reading are never cut off. */
  static visibleFileRows(
    files: readonly IDataRoomFileProgress[]
  ): IDataRoomFileProgress[] {
    const order = [
      DataRoomFileState.FAILED,
      DataRoomFileState.UPLOADING,
      DataRoomFileState.STORED,
    ];
    return [...files]
      .sort(
        (first, second) =>
          order.indexOf(first.state) - order.indexOf(second.state)
      )
      .slice(0, DataRoomUploadHelper.MAX_VISIBLE_FILE_ROWS);
  }

  private static matchKey(
    name: string,
    sizeBytes: number,
    lastModified: number | null
  ): string {
    return lastModified === null
      ? `${name}|${sizeBytes}`
      : `${name}|${sizeBytes}|${lastModified}`;
  }

  /** A file id is `<batchId>-<position>`, its place in the batch request. */
  private static filePosition(fileId: string): number {
    return Number(fileId.slice(fileId.lastIndexOf('-') + 1));
  }
}
